import { type AdvancedFilterState } from '../types';

import {
  ENTITLEMENT_LIST_SORT_ACTIONS,
  type EntitlementListSortFieldState,
} from './entitlements';
import { ADVANCED_FILTERS_METHODS } from './global';

export const USERS_INI_CALL: Record<string, string> = {
  FILTERS: 'USERS_FILTERS',
  COLUMNS: 'USERS_COLUMNS',
};

const USERS_INITIAL_FILTERS: AdvancedFilterState[] = [
  {
    label: 'Username',
    value: 'username',
    category: 'User',
    method: 'search',
    isChecked: false,
    isDisabled: false,
  },
  {
    label: 'First Name',
    value: 'firstname',
    category: 'User',
    method: 'search',
    isChecked: false,
    isDisabled: false,
  },
  {
    label: 'Last Name',
    value: 'lastname',
    category: 'User',
    method: 'search',
    isChecked: false,
    isDisabled: false,
  },
  {
    label: 'Status',
    value: 'status',
    category: 'User',
    method: 'search',
    isChecked: false,
    isDisabled: false,
  },
  {
    label: 'Display Name',
    value: 'displayname',
    category: 'User',
    method: 'search',
    isChecked: false,
    isDisabled: false,
  },
  {
    label: 'Department Name',
    value: 'departmentname',
    category: 'Department',
    method: 'search',
    isChecked: false,
    isDisabled: false,
  },
  {
    label: 'Email',
    value: 'email',
    category: 'User',
    method: 'search',
    isChecked: false,
    isDisabled: false,
  },
];

export default USERS_INITIAL_FILTERS;

export const ENTITLEMENT_USER_LIST_AD_FILTER_CAT: Record<string, string> = {
  USER: 'User',
  DEPARTMENT: 'Department',
  STATUS: 'Status',
} as const;

export const ENTITLEMENT_USER_LIST_AD_FILTER: Record<
  string,
  AdvancedFilterState
> = {
  USERNAME: {
    label: 'Username',
    value: 'username',
    category: ENTITLEMENT_USER_LIST_AD_FILTER_CAT.USER,
    method: ADVANCED_FILTERS_METHODS.SEARCH,
  },
  FIRSTNAME: {
    label: 'First Name',
    value: 'firstname',
    category: ENTITLEMENT_USER_LIST_AD_FILTER_CAT.USER,
    method: ADVANCED_FILTERS_METHODS.SEARCH,
  },
  LASTNAME: {
    label: 'Last Name',
    value: 'lastname',
    category: ENTITLEMENT_USER_LIST_AD_FILTER_CAT.USER,
    method: ADVANCED_FILTERS_METHODS.SEARCH,
  },
  EMAIL: {
    label: 'Email',
    value: 'email',
    category: ENTITLEMENT_USER_LIST_AD_FILTER_CAT.USER,
    method: ADVANCED_FILTERS_METHODS.SEARCH,
  },
  STATUS: {
    label: 'Status',
    value: 'status',
    category: ENTITLEMENT_USER_LIST_AD_FILTER_CAT.STATUS,
    method: ADVANCED_FILTERS_METHODS.FIXED,
    options: [
      {
        label: 'Active',
        value: 'ACTIVE',
        isChecked: false,
      },
      {
        label: 'Inactive',
        value: 'INACTIVE',
        isChecked: false,
      },
    ],
  },
  DISPLAYNAME: {
    label: 'Display Name',
    value: 'displayname',
    category: ENTITLEMENT_USER_LIST_AD_FILTER_CAT.USER,
    method: ADVANCED_FILTERS_METHODS.SEARCH,
  },
  DEPARTMENTNAME: {
    label: 'Department Name',
    value: 'departmentname',
    category: ENTITLEMENT_USER_LIST_AD_FILTER_CAT.DEPARTMENT,
    method: ADVANCED_FILTERS_METHODS.SEARCH,
  },
} as const;

export const DEFAULT_USER_FILTERS: string[] = [
  ENTITLEMENT_USER_LIST_AD_FILTER.USERNAME.value,
  ENTITLEMENT_USER_LIST_AD_FILTER.FIRSTNAME.value,
  ENTITLEMENT_USER_LIST_AD_FILTER.LASTNAME.value,
  ENTITLEMENT_USER_LIST_AD_FILTER.STATUS.value,
  ENTITLEMENT_USER_LIST_AD_FILTER.DISPLAYNAME.value,
  ENTITLEMENT_USER_LIST_AD_FILTER.DEPARTMENTNAME.value,
  ENTITLEMENT_USER_LIST_AD_FILTER.EMAIL.value,
] as const;

export const ENTITLEMENT_USER_LIST_INITIAL_FILTERS: AdvancedFilterState[] =
  Object.values(ENTITLEMENT_USER_LIST_AD_FILTER).map((filter) => {
    const isDefaultFilter = DEFAULT_USER_FILTERS.includes(filter.value);

    return {
      ...filter,
      isDisabled: false,
      isChecked: isDefaultFilter,
    };
  });

export const ENTITLEMENT_USER_LIST_SORT_FIELDS: Record<
  string,
  EntitlementListSortFieldState
> = {
  USERNAME: {
    label: 'Username',
    value: 'userName',
    options: [
      ENTITLEMENT_LIST_SORT_ACTIONS.ATOZ.value,
      ENTITLEMENT_LIST_SORT_ACTIONS.ZTOA.value,
    ],
  },
  DISPLAYNAME: {
    label: 'Display Name',
    value: 'displayName',
    options: [
      ENTITLEMENT_LIST_SORT_ACTIONS.ATOZ.value,
      ENTITLEMENT_LIST_SORT_ACTIONS.ZTOA.value,
    ],
  },
  FIRSTNAME: {
    label: 'First Name',
    value: 'firstName',
    options: [
      ENTITLEMENT_LIST_SORT_ACTIONS.ATOZ.value,
      ENTITLEMENT_LIST_SORT_ACTIONS.ZTOA.value,
    ],
  },
  LASTNAME: {
    label: 'Last Name',
    value: 'lastName',
    options: [
      ENTITLEMENT_LIST_SORT_ACTIONS.ATOZ.value,
      ENTITLEMENT_LIST_SORT_ACTIONS.ZTOA.value,
    ],
  },
  EMAIL: {
    label: 'Email',
    value: 'email',
    options: [
      ENTITLEMENT_LIST_SORT_ACTIONS.ATOZ.value,
      ENTITLEMENT_LIST_SORT_ACTIONS.ZTOA.value,
    ],
  },
  DEPARTMENT: {
    label: 'Department',
    value: 'departmentName',
    options: [
      ENTITLEMENT_LIST_SORT_ACTIONS.ATOZ.value,
      ENTITLEMENT_LIST_SORT_ACTIONS.ZTOA.value,
    ],
  },
  SYSTEMUSERNAME: {
    label: 'System Username',
    value: 'systemUserName',
    options: [
      ENTITLEMENT_LIST_SORT_ACTIONS.ATOZ.value,
      ENTITLEMENT_LIST_SORT_ACTIONS.ZTOA.value,
    ],
  },
  EMPLOYEEID: {
    label: 'Employee ID',
    value: 'employeeId',
    options: [
      ENTITLEMENT_LIST_SORT_ACTIONS.ATOZ.value,
      ENTITLEMENT_LIST_SORT_ACTIONS.ZTOA.value,
    ],
  },
};

export const DEFAULT_USER_COLUMNS = [
  'userName',
  'firstName',
  'lastName',
  'email',
  'status',
];
